import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AuthProvider,
  CatalogCollection,
  CharacterCategory,
  CharacterType,
  Locale,
  Plan,
  StoryMode,
  StoryStatus,
  UserRole,
} from './gql/graphql';

/**
 * Everything the schema names in English and the console shows in the
 * operator's language, in one file — so a wording change is one edit rather
 * than a search across pages, and so a new enum member shows up as a type error
 * here first.
 *
 * The text itself lives in `src/i18n/{ru,en}.json` under `labels.*`; what this
 * file owns is the mapping from a schema value to its key, which is where the
 * exhaustiveness check has to be.
 */

const PLAN_KEYS: Record<Plan, string> = {
  free: 'labels.plan.free',
  plus: 'labels.plan.plus',
};

const ROLE_KEYS: Record<UserRole, string> = {
  parent: 'labels.role.parent',
  kid: 'labels.role.kid',
};

const PROVIDER_KEYS: Record<AuthProvider, string> = {
  apple: 'labels.provider.apple',
  google: 'labels.provider.google',
  facebook: 'labels.provider.facebook',
  email: 'labels.provider.email',
};

const STORY_MODE_KEYS: Record<StoryMode, string> = {
  self: 'labels.storyMode.self',
  ai: 'labels.storyMode.ai',
  sequel: 'labels.storyMode.sequel',
};

const STORY_STATUS_KEYS: Record<StoryStatus, string> = {
  queued: 'labels.storyStatus.queued',
  generating: 'labels.storyStatus.generating',
  ready: 'labels.storyStatus.ready',
  failed: 'labels.storyStatus.failed',
};

const CHARACTER_CATEGORY_KEYS: Record<CharacterCategory, string> = {
  people: 'labels.characterCategory.people',
  animals: 'labels.characterCategory.animals',
  other: 'labels.characterCategory.other',
};

const COLLECTION_KEYS: Record<CatalogCollection, string> = {
  characters: 'labels.collection.characters',
  places: 'labels.collection.places',
  conflicts: 'labels.collection.conflicts',
  morals: 'labels.collection.morals',
  traits: 'labels.collection.traits',
};

const LOCALE_KEYS: Record<Locale, string> = {
  ru: 'labels.locale.ru',
  en: 'labels.locale.en',
};

/** Mantine colour per status: the dashboard and the tables must agree. */
export const STORY_STATUS_COLORS: Record<StoryStatus, string> = {
  queued: 'gray',
  generating: 'blue',
  ready: 'teal',
  failed: 'red',
};

/** The five catalogue tabs, in the order the wizard walks them. */
export const COLLECTIONS: CatalogCollection[] = [
  'characters',
  'places',
  'conflicts',
  'morals',
  'traits',
];

const CHARACTER_TYPE_KEYS: Record<CharacterType, string> = {
  hero: 'labels.characterType.hero',
  villain: 'labels.characterType.villain',
  helper: 'labels.characterType.helper',
};

/** The option lists the filters and the forms offer, in a deliberate order. */
export const PLANS: Plan[] = ['free', 'plus'];
export const USER_ROLES: UserRole[] = ['parent', 'kid'];
export const STORY_MODES: StoryMode[] = ['self', 'ai', 'sequel'];
export const STORY_STATUSES: StoryStatus[] = ['queued', 'generating', 'ready', 'failed'];
export const CHARACTER_CATEGORIES: CharacterCategory[] = ['people', 'animals', 'other'];
export const CHARACTER_TYPES: CharacterType[] = ['hero', 'villain', 'helper'];

export type Labels = {
  plan: (value: Plan) => string;
  role: (value: UserRole) => string;
  provider: (value: AuthProvider) => string;
  /** A library story has no wizard draft behind it, so no mode: an em dash. */
  storyMode: (value: StoryMode | null) => string;
  storyStatus: (value: StoryStatus) => string;
  characterCategory: (value: CharacterCategory) => string;
  characterType: (value: CharacterType) => string;
  collection: (value: CatalogCollection) => string;
  locale: (value: Locale) => string;
  /** A timestamp in the operator's language, or an em dash when there is none. */
  dateTime: (value: string | null | undefined) => string;
};

/**
 * The labels, bound to the current language. A hook rather than a set of maps
 * because the answer changes when the header's switch does, and a component
 * that read a module-level map would keep the old wording until it re-rendered
 * for some other reason.
 */
export function useLabels(): Labels {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return useMemo(() => {
    const dateTime = new Intl.DateTimeFormat(language, {
      dateStyle: 'short',
      timeStyle: 'short',
    });

    return {
      plan: (value) => t(PLAN_KEYS[value]),
      role: (value) => t(ROLE_KEYS[value]),
      provider: (value) => t(PROVIDER_KEYS[value]),
      storyMode: (value) => (value ? t(STORY_MODE_KEYS[value]) : t('common.empty')),
      storyStatus: (value) => t(STORY_STATUS_KEYS[value]),
      characterCategory: (value) => t(CHARACTER_CATEGORY_KEYS[value]),
      characterType: (value) => t(CHARACTER_TYPE_KEYS[value]),
      collection: (value) => t(COLLECTION_KEYS[value]),
      locale: (value) => t(LOCALE_KEYS[value]),
      dateTime: (value) => {
        if (!value) return t('common.empty');

        const date = new Date(value);

        return Number.isNaN(date.getTime()) ? t('common.empty') : dateTime.format(date);
      },
    };
  }, [t, language]);
}

export function isCatalogCollection(value: string | undefined): value is CatalogCollection {
  return !!value && (COLLECTIONS as string[]).includes(value);
}

/** Which of the five tables actually store artwork (morals and traits do not). */
export function hasArtwork(collection: CatalogCollection): boolean {
  return collection === 'characters' || collection === 'places' || collection === 'conflicts';
}

/** Only characters carry a category, and there it is required. */
export function hasCategory(collection: CatalogCollection): boolean {
  return collection === 'characters';
}

/** Only characters carry a character type, and there it is required. */
export function hasCharacterType(collection: CatalogCollection): boolean {
  return collection === 'characters';
}

/** Not stored for morals and traits, same as the artwork. */
export function hasDescription(collection: CatalogCollection): boolean {
  return hasArtwork(collection);
}

/** Only characters carry a prompt description, fed to the story prompt. */
export function hasPromptDescription(collection: CatalogCollection): boolean {
  return hasCategory(collection);
}

/** A uuid is too wide for a table cell and its first block is enough to match on. */
export function shortId(id: string): string {
  return id.split('-')[0] ?? id;
}
